import * as React from 'react';

import { Card, CardContent } from './card';
import { cn } from '@frontend/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

const metricCardVariants = cva(
  'h-[120px] cursor-pointer transition-all duration-200 border',
  {
    variants: {
      colorScheme: {
        gray: 'border-gray-200 hover:border-gray-300',
        blue: 'border-blue-200 hover:border-blue-300',
        green: 'border-green-200 hover:border-green-300',
        red: 'border-red-200 hover:border-red-300',
        orange: 'border-orange-200 hover:border-orange-300',
      },
      isActive: {
        true: '',
        false: 'bg-white',
      },
    },
    compoundVariants: [
      {
        colorScheme: 'gray',
        isActive: true,
        className: 'bg-gray-50 border-gray-200 border-2',
      },
      {
        colorScheme: 'blue',
        isActive: true,
        className: 'bg-blue-50 border-blue-200 border-2',
      },
      {
        colorScheme: 'green',
        isActive: true,
        className: 'bg-green-50 border-green-200 border-2',
      },
      {
        colorScheme: 'red',
        isActive: true,
        className: 'bg-red-50 border-red-200 border-2',
      },
      {
        colorScheme: 'orange',
        isActive: true,
        className: 'bg-orange-50 border-orange-200 border-2',
      },
    ],
    defaultVariants: {
      colorScheme: 'gray',
      isActive: false,
    },
  },
);

const metricCardHoverVariants = cva('', {
  variants: {
    colorScheme: {
      gray: 'hover:bg-gray-50 hover:shadow-md hover:-translate-y-0.5',
      blue: 'hover:bg-blue-50 hover:shadow-md hover:-translate-y-0.5',
      green: 'hover:bg-green-50 hover:shadow-md hover:-translate-y-0.5',
      red: 'hover:bg-red-50 hover:shadow-md hover:-translate-y-0.5',
      orange: 'hover:bg-orange-50 hover:shadow-md hover:-translate-y-0.5',
    },
    isActive: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      colorScheme: 'gray',
      isActive: true,
      className: 'hover:bg-gray-100',
    },
    {
      colorScheme: 'blue',
      isActive: true,
      className: 'hover:bg-blue-100',
    },
    {
      colorScheme: 'green',
      isActive: true,
      className: 'hover:bg-green-100',
    },
    {
      colorScheme: 'red',
      isActive: true,
      className: 'hover:bg-red-100',
    },
    {
      colorScheme: 'orange',
      isActive: true,
      className: 'hover:bg-orange-100',
    },
  ],
});

const metricValueVariants = cva('text-3xl font-normal', {
  variants: {
    colorScheme: {
      gray: 'text-gray-600',
      blue: 'text-blue-600',
      green: 'text-green-600',
      red: 'text-red-600',
      orange: 'text-orange-600',
    },
    isActive: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      colorScheme: 'gray',
      isActive: true,
      className: 'text-gray-700',
    },
    {
      colorScheme: 'blue',
      isActive: true,
      className: 'text-blue-700',
    },
    {
      colorScheme: 'green',
      isActive: true,
      className: 'text-green-700',
    },
    {
      colorScheme: 'red',
      isActive: true,
      className: 'text-red-700',
    },
    {
      colorScheme: 'orange',
      isActive: true,
      className: 'text-orange-700',
    },
  ],
});

export interface MetricCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof metricCardVariants> {
  title: string;
  value: number;
  icon: React.ReactNode;
  isActive?: boolean;
}

const MetricCard = React.forwardRef<HTMLDivElement, MetricCardProps>(
  (
    { className, colorScheme, isActive, title, value, icon, onClick, ...props },
    ref,
  ) => {
    const clickable = !!onClick;

    return (
      <Card
        ref={ref}
        className={cn(
          metricCardVariants({ colorScheme, isActive }),
          clickable && metricCardHoverVariants({ colorScheme, isActive }),
          clickable && 'cursor-pointer',
          !clickable && 'cursor-default',
          'py-0 gap-0 flex items-center justify-center',
          className,
        )}
        onClick={onClick}
        {...props}
      >
        <CardContent className="p-0 flex items-center justify-center h-full">
          <div className="text-center">
            <div className="flex justify-center mb-1">
              <span
                className={cn(metricValueVariants({ colorScheme, isActive }))}
                style={{ fontSize: '32px' }}
              >
                {value.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-center items-center space-x-2">
              {icon && (
                <div style={{ width: '16px', height: '16px' }}>
                  {React.cloneElement(icon as React.ReactElement, {
                    size: 16,
                    style: { width: '16px', height: '16px' },
                  })}
                </div>
              )}
              <div className="text-gray-600" style={{ fontSize: '13px' }}>
                {title}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  },
);

MetricCard.displayName = 'MetricCard';

export { MetricCard, metricCardVariants };
